import React, { useState } from "react";
import { 
    CheckCircle, AlertCircle, Info, AlertOctagon, Copy, Check, Terminal, Cpu 
} from "react-feather";
import styles from "./MarkdownContentRenderer.module.css";

// Multi-language keyword highlighter helper
const highlightCodeSyntax = (codeText, lang = "systemverilog") => {
    if (!codeText) return "";

    const keywordsByLang = {
        verilog: [
            "module", "endmodule", "input", "output", "inout", "reg", "wire", "logic", 
            "parameter", "localparam", "initial", "always", "always_ff", "always_comb", 
            "always_latch", "begin", "end", "assign", "posedge", "negedge", "case", "endcase", 
            "default", "if", "else", "for", "while", "task", "endtask", "function", "endfunction"
        ],
        systemverilog: [
            "typedef", "enum", "struct", "union", "class", "endclass", "extends", "virtual", 
            "interface", "endinterface", "package", "endpackage", "import", "export", "rand", 
            "randc", "constraint", "covergroup", "coverpoint", "property", "endproperty", 
            "assert", "cover", "assume", "bit", "int", "byte", "shortint", "longint", "string", 
            "module", "endmodule", "input", "output", "logic", "always_ff", "always_comb", 
            "always_latch", "begin", "end", "unique", "priority", "inside", "foreach", "return",
            "parameter", "localparam", "initial", "always", "assign", "posedge", "negedge",
            "case", "endcase", "default", "if", "else", "for", "while", "task", "endtask",
            "function", "endfunction"
        ],
        vhdl: [
            "entity", "architecture", "is", "of", "begin", "end", "port", "in", "out", "inout", 
            "signal", "variable", "constant", "process", "if", "then", "else", "elsif", "case", 
            "when", "type", "subtype", "array", "record", "component", "generic", "map"
        ],
        cpp: [
            "class", "public", "private", "protected", "struct", "namespace", "using", "template", 
            "typename", "auto", "const", "constexpr", "int", "char", "float", "double", "bool", 
            "void", "return", "if", "else", "for", "while", "do", "switch", "case", "include"
        ],
        python: [
            "def", "class", "import", "from", "as", "return", "if", "elif", "else", "for", 
            "while", "try", "except", "finally", "with", "yield", "lambda", "global", "nonlocal", 
            "True", "False", "None", "and", "or", "not", "in", "is"
        ]
    };

    const targetLang = lang?.toLowerCase() || "systemverilog";
    const keywordsList = keywordsByLang[targetLang] || keywordsByLang.systemverilog;
    const keywordsSet = new Set(keywordsList);

    const escapeHtml = (str) => {
        if (!str) return "";
        return str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    };

    let masterRegex;
    if (targetLang === "python" || targetLang === "cpp" || targetLang === "c") {
        masterRegex = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/|#[^\n]*)|("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(\b\d+(?:\.\d+)?\b|\b0x[0-9a-fA-F]+\b)|([a-zA-Z_][a-zA-Z0-9_]*)/g;
    } else {
        // verilog, systemverilog, vhdl, default
        // Note: single quotes are used for literals like 8'hFF, 1'b0, '0, '1
        masterRegex = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|("(?:\\.|[^"\\])*")|(\b\d+'[sS]?[bBoOdDhH][0-9a-fA-F_]+\b|\b\d+'[01xzXZ]\b|'[01xzXZ]\b|\b\d+(?:\.\d+)?\b|\b0x[0-9a-fA-F]+\b)|([a-zA-Z_][a-zA-Z0-9_$]*)/g;
    }

    let lastIndex = 0;
    let htmlResult = "";

    codeText.replace(masterRegex, (match, comment, str, num, word, offset) => {
        if (offset > lastIndex) {
            htmlResult += escapeHtml(codeText.slice(lastIndex, offset));
        }
        lastIndex = offset + match.length;

        if (comment) {
            htmlResult += `<span class="${styles.codeComment}">${escapeHtml(comment)}</span>`;
        } else if (str) {
            htmlResult += `<span class="${styles.codeString}">${escapeHtml(str)}</span>`;
        } else if (num) {
            htmlResult += `<span class="${styles.codeNumber}">${escapeHtml(num)}</span>`;
        } else if (word) {
            if (keywordsSet.has(word)) {
                htmlResult += `<span class="${styles.codeKeyword}">${escapeHtml(word)}</span>`;
            } else {
                htmlResult += escapeHtml(word);
            }
        } else {
            htmlResult += escapeHtml(match);
        }
        return match;
    });

    if (lastIndex < codeText.length) {
        htmlResult += escapeHtml(codeText.slice(lastIndex));
    }

    return htmlResult;
};

const CodeBlockContainer = ({ code, lang = "systemverilog", title, onRun }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const lines = code.trim().split("\n");

    return (
        <div className={styles.codeWrapper}>
            <div className={styles.codeHeader}>
                <div className={styles.codeTitleGroup}>
                    <Terminal size={14} className={styles.codeIcon} />
                    <span className={styles.codeLangBadge}>{lang.toUpperCase()}</span>
                    {title && <span className={styles.codeTitle}>{title}</span>}
                </div>
                <div className={styles.codeHeaderActions}>
                    <button type="button" onClick={handleCopy} className={styles.codeActionBtn} title="Copy Code">
                        {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                        <span>{copied ? "Copied!" : "Copy"}</span>
                    </button>
                </div>
            </div>
            <div className={styles.codeBody}>
                <div className={styles.lineNumbers}>
                    {lines.map((_, idx) => (
                        <span key={idx}>{idx + 1}</span>
                    ))}
                </div>
                <div className={styles.codeLines}>
                    {lines.map((line, idx) => (
                        <div 
                            key={idx} 
                            className={styles.codeLine}
                            dangerouslySetInnerHTML={{ __html: highlightCodeSyntax(line, lang) || "&nbsp;" }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const MarkdownContentRenderer = ({ content = "" }) => {
    if (!content && content !== 0) return null;
    const safeContent = typeof content === "string" ? content : String(content);

    const formatInlineText = (text) => {
        if (!text) return "";
        let formatted = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

        // Markdown Images: ![alt](url) - strictly sanitize URL scheme & escape quotes
        formatted = formatted.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
            const cleanUrl = url.trim().replace(/"/g, "&quot;");
            const cleanAlt = alt.replace(/"/g, "&quot;");
            if (/^(https?:\/\/|\/|\.\/|data:image\/)/i.test(cleanUrl)) {
                return `<img src="${cleanUrl}" alt="${cleanAlt}" class="${styles.markdownImage}" />`;
            }
            return cleanAlt;
        });

        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
        formatted = formatted.replace(/`([^`]+)`/g, `<code class="${styles.inlineBadge}">$1</code>`);

        return formatted;
    };

    const parseBlocks = (rawText) => {
        const blocks = [];
        if (!rawText) return blocks;
        const lines = String(rawText).split(/\r?\n/);
        let i = 0;

        while (i < lines.length) {
            const line = lines[i];

            if (line.trim().startsWith(":::callout")) {
                const typeMatch = line.match(/type="([^"]+)"/);
                const titleMatch = line.match(/title="([^"]+)"/);
                const type = typeMatch ? typeMatch[1] : "info";
                const title = titleMatch ? titleMatch[1] : "";
                
                let bodyLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith(":::")) {
                    bodyLines.push(lines[i]);
                    i++;
                }
                blocks.push({
                    type: "callout",
                    calloutType: type,
                    title,
                    content: bodyLines.join("\n").trim()
                });
                i++;
                continue;
            }

            if (line.trim().startsWith(":::card-grid")) {
                const colsMatch = line.match(/columns="([^"]+)"/);
                const columns = colsMatch ? parseInt(colsMatch[1]) : 3;

                let cardLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith(":::")) {
                    cardLines.push(lines[i]);
                    i++;
                }

                const cardsText = cardLines.join("\n");
                const cardRegex = /\[card(?:\s+icon="([^"]+)")?(?:\s+title="([^"]+)")?\]([\s\S]*?)\[\/card\]/g;
                let cardMatch;
                const cards = [];

                while ((cardMatch = cardRegex.exec(cardsText)) !== null) {
                    cards.push({
                        icon: cardMatch[1] || "cpu",
                        title: cardMatch[2] || "",
                        text: cardMatch[3].trim()
                    });
                }

                blocks.push({
                    type: "card-grid",
                    columns,
                    cards
                });
                i++;
                continue;
            }

            if (line.trim().startsWith(":::category-columns")) {
                let colLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith(":::")) {
                    colLines.push(lines[i]);
                    i++;
                }

                const catText = colLines.join("\n");
                const catRegex = /\[category(?:\s+title="([^"]+)")?(?:\s+color="([^"]+)")?\]([\s\S]*?)\[\/category\]/g;
                let catMatch;
                const categories = [];

                while ((catMatch = catRegex.exec(catText)) !== null) {
                    const items = catMatch[3]
                        .split("\n")
                        .map(l => l.trim().replace(/^[-*]\s*/, ''))
                        .filter(l => l);

                    categories.push({
                        title: catMatch[1] || "",
                        color: catMatch[2] || "blue",
                        items
                    });
                }

                blocks.push({
                    type: "category-columns",
                    categories
                });
                i++;
                continue;
            }

            if (line.trim().startsWith(":::code-comparison")) {
                const lTitleMatch = line.match(/left-title="([^"]+)"/);
                const rTitleMatch = line.match(/right-title="([^"]+)"/);
                const lLangMatch = line.match(/left-lang="([^"]+)"/);
                const rLangMatch = line.match(/right-lang="([^"]+)"/);

                const leftTitle = lTitleMatch ? lTitleMatch[1] : "Legacy";
                const rightTitle = rTitleMatch ? rTitleMatch[1] : "Modern";
                const leftLang = lLangMatch ? lLangMatch[1] : "verilog";
                const rightLang = rLangMatch ? rLangMatch[1] : "systemverilog";

                let compLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith(":::")) {
                    compLines.push(lines[i]);
                    i++;
                }

                const fullCompText = compLines.join("\n");
                const leftMatch = fullCompText.match(/\[left\]([\s\S]*?)\[\/left\]/);
                const rightMatch = fullCompText.match(/\[right\]([\s\S]*?)\[\/right\]/);

                blocks.push({
                    type: "code-comparison",
                    leftTitle,
                    rightTitle,
                    leftLang,
                    rightLang,
                    leftCode: leftMatch ? leftMatch[1].trim() : "",
                    rightCode: rightMatch ? rightMatch[1].trim() : ""
                });
                i++;
                continue;
            }

            if (line.trim().startsWith(":::prerequisites")) {
                let prereqLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith(":::")) {
                    prereqLines.push(lines[i]);
                    i++;
                }

                const prereqText = prereqLines.join("\n");
                const panelRegex = /\[panel(?:\s+title="([^"]+)")?(?:\s+color="([^"]+)")?\]([\s\S]*?)\[\/panel\]/g;
                let panelMatch;
                const panels = [];

                while ((panelMatch = panelRegex.exec(prereqText)) !== null) {
                    const items = panelMatch[3]
                        .split("\n")
                        .map(l => l.trim().replace(/^[-*]\s*/, ''))
                        .filter(l => l);

                    panels.push({
                        title: panelMatch[1] || "",
                        color: panelMatch[2] || "green",
                        items
                    });
                }

                blocks.push({
                    type: "prerequisites",
                    panels
                });
                i++;
                continue;
            }

            if (line.trim().startsWith("```")) {
                const lang = line.trim().replace(/^```/, "").trim() || "systemverilog";
                let codeLines = [];
                i++;
                while (i < lines.length && !lines[i].trim().startsWith("```")) {
                    codeLines.push(lines[i]);
                    i++;
                }
                blocks.push({
                    type: "code-block",
                    lang,
                    code: codeLines.join("\n")
                });
                i++;
                continue;
            }

            const trimmedLine = line.trim();
            if (trimmedLine.startsWith("#")) {
                const headingMatch = trimmedLine.match(/^#+/);
                const level = headingMatch ? headingMatch[0].length : 1;
                const title = trimmedLine.replace(/^#+\s*/, "").trim();
                blocks.push({ type: "heading", level, text: title });
                i++;
                continue;
            }

            if (line.trim().startsWith("- ") || line.trim().startsWith("* ")) {
                let listItems = [];
                while (i < lines.length && (lines[i].trim().startsWith("- ") || lines[i].trim().startsWith("* "))) {
                    listItems.push(lines[i].trim().replace(/^[-*]\s*/, ""));
                    i++;
                }
                blocks.push({ type: "list", items: listItems });
                continue;
            }

            if (line.trim().length > 0) {
                blocks.push({ type: "paragraph", text: line });
            }

            i++;
        }

        return blocks;
    };

    const blocks = parseBlocks(safeContent);

    return (
        <div className={styles.markdownRenderer}>
            {blocks.map((block, idx) => {
                switch (block.type) {
                    case "heading": {
                        const HeadingTag = `h${Math.min(block.level + 1, 6)}`;
                        return (
                            <HeadingTag key={idx} className={styles[`heading${block.level}`]}>
                                {block.text}
                            </HeadingTag>
                        );
                    }

                    case "callout": {
                        const calloutIcons = {
                            success: <CheckCircle size={20} className={styles.iconSuccess} />,
                            info: <Info size={20} className={styles.iconInfo} />,
                            warning: <AlertCircle size={20} className={styles.iconWarning} />,
                            danger: <AlertOctagon size={20} className={styles.iconDanger} />
                        };
                        return (
                            <div key={idx} className={`${styles.calloutBox} ${styles[`callout_${block.calloutType}`]}`}>
                                <div className={styles.calloutHeader}>
                                    {calloutIcons[block.calloutType] || calloutIcons.info}
                                    {block.title && <h5 className={styles.calloutTitle}>{block.title}</h5>}
                                </div>
                                <div 
                                    className={styles.calloutBody}
                                    dangerouslySetInnerHTML={{ __html: formatInlineText(block.content) }}
                                />
                            </div>
                        );
                    }

                    case "card-grid": {
                        return (
                            <div 
                                key={idx} 
                                className={styles.cardGrid}
                                style={{ gridTemplateColumns: `repeat(${block.columns}, 1fr)` }}
                            >
                                {block.cards.map((card, cIdx) => (
                                    <div key={cIdx} className={styles.gridCard}>
                                        <div className={styles.cardIconWrapper}>
                                            <Cpu size={22} />
                                        </div>
                                        <h5 className={styles.cardTitle}>{card.title}</h5>
                                        <p 
                                            className={styles.cardText}
                                            dangerouslySetInnerHTML={{ __html: formatInlineText(card.text) }}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    case "category-columns": {
                        return (
                            <div key={idx} className={styles.categoryGrid}>
                                {block.categories.map((cat, cIdx) => (
                                    <div key={cIdx} className={`${styles.categoryCard} ${styles[`catBorder_${cat.color}`]}`}>
                                        <h5 className={`${styles.categoryHeader} ${styles[`catHeader_${cat.color}`]}`}>
                                            {cat.title}
                                        </h5>
                                        <ul className={styles.categoryList}>
                                            {cat.items.map((item, iIdx) => (
                                                <li 
                                                    key={iIdx}
                                                    dangerouslySetInnerHTML={{ __html: formatInlineText(item) }}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    case "code-comparison": {
                        return (
                            <div key={idx} className={styles.comparisonContainer}>
                                <div className={styles.comparisonGrid}>
                                    <div className={styles.comparisonPane}>
                                        <CodeBlockContainer 
                                            code={block.leftCode} 
                                            lang={block.leftLang} 
                                            title={block.leftTitle}
                                        />
                                    </div>
                                    <div className={styles.comparisonPane}>
                                        <CodeBlockContainer 
                                            code={block.rightCode} 
                                            lang={block.rightLang} 
                                            title={block.rightTitle}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    case "prerequisites": {
                        return (
                            <div key={idx} className={styles.prereqContainer}>
                                {block.panels.map((panel, pIdx) => (
                                    <div key={pIdx} className={`${styles.prereqCard} ${styles[`prereq_${panel.color}`]}`}>
                                        <h5 className={styles.prereqTitle}>{panel.title}</h5>
                                        <ul className={styles.prereqList}>
                                            {panel.items.map((item, iIdx) => (
                                                <li 
                                                    key={iIdx}
                                                    dangerouslySetInnerHTML={{ __html: formatInlineText(item) }}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    case "code-block": {
                        return (
                            <CodeBlockContainer 
                                key={idx}
                                code={block.code}
                                lang={block.lang}
                            />
                        );
                    }

                    case "list": {
                        return (
                            <ul key={idx} className={styles.markdownList}>
                                {block.items.map((item, iIdx) => (
                                    <li 
                                        key={iIdx}
                                        dangerouslySetInnerHTML={{ __html: formatInlineText(item) }}
                                    />
                                ))}
                            </ul>
                        );
                    }

                    case "paragraph":
                    default: {
                        return (
                            <div 
                                key={idx}
                                className={styles.paragraph}
                                dangerouslySetInnerHTML={{ __html: formatInlineText(block.text) }}
                            />
                        );
                    }
                }
            })}
        </div>
    );
};

export default MarkdownContentRenderer;
